import {useEffect, useRef, useState} from 'react';
import {Box, Collapse, FormControlLabel, Switch, Typography} from '@mui/material';
import {InputField} from '@/components/common/InputField';

export interface WpIssueMarkState {
    marked: boolean;
    remark: string;
    outstanding: boolean;
    outstandingRemark: string;
    valid: boolean;
}

export interface WpIssueMarkControlProps {
    code: string;
    defaultMarked?: boolean;
    defaultRemark?: string | null;
    defaultOutstanding?: boolean;
    defaultOutstandingRemark?: string | null;
    onChange?: (state: WpIssueMarkState) => void;
    disabled?: boolean;
}

export function WpIssueMarkControl({
    code,
    defaultMarked = false,
    defaultRemark = '',
    defaultOutstanding = false,
    defaultOutstandingRemark = '',
    onChange,
    disabled = false,
}: WpIssueMarkControlProps) {
    const [marked, setMarked] = useState(defaultMarked);
    const [remark, setRemark] = useState(defaultRemark ?? '');
    const [outstanding, setOutstanding] = useState(defaultOutstanding);
    const [outstandingRemark, setOutstandingRemark] = useState(defaultOutstandingRemark ?? '');
    /** After user edits, do not re-hydrate from server defaults until schedule/code changes. */
    const dirtyRef = useRef(false);
    const lastCodeRef = useRef(code);

    useEffect(() => {
        if (lastCodeRef.current !== code) {
            lastCodeRef.current = code;
            dirtyRef.current = false;
        }
        if (dirtyRef.current) {
            return;
        }
        setMarked(defaultMarked);
        setRemark(defaultRemark ?? '');
        setOutstanding(defaultOutstanding);
        setOutstandingRemark(defaultOutstandingRemark ?? '');
    }, [code, defaultMarked, defaultRemark, defaultOutstanding, defaultOutstandingRemark]);

    useEffect(() => {
        const issueValid = !marked || remark.trim().length > 0;
        const outstandingValid = !outstanding || outstandingRemark.trim().length > 0;
        onChange?.({
            marked,
            remark,
            outstanding,
            outstandingRemark,
            valid: issueValid && outstandingValid,
        });
    }, [marked, remark, outstanding, outstandingRemark, onChange]);

    return (
        <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 240}}>
            <FormControlLabel
                control={
                    <Switch
                        checked={marked}
                        onChange={(_, checked) => {
                            dirtyRef.current = true;
                            setMarked(checked);
                        }}
                        disabled={disabled}
                        size="small"
                    />
                }
                label={<Typography variant="body2">Mark as issue</Typography>}
            />
            <Collapse in={marked} timeout="auto" unmountOnExit>
                <Box sx={{pt: 0.5}}>
                    <InputField
                        label="Issue description"
                        value={remark}
                        onChange={(e) => {
                            dirtyRef.current = true;
                            setRemark(e.target.value);
                        }}
                        required
                        display={disabled}
                        fullWidth
                        size="small"
                        multiline
                        minRows={2}
                    />
                </Box>
            </Collapse>

            <FormControlLabel
                control={
                    <Switch
                        checked={outstanding}
                        onChange={(_, checked) => {
                            dirtyRef.current = true;
                            setOutstanding(checked);
                        }}
                        disabled={disabled}
                        size="small"
                    />
                }
                label={<Typography variant="body2">Outstanding to client</Typography>}
            />
            <Collapse in={outstanding} timeout="auto" unmountOnExit>
                <Box sx={{pt: 0.5}}>
                    <InputField
                        label="Outstanding description"
                        value={outstandingRemark}
                        onChange={(e) => {
                            dirtyRef.current = true;
                            setOutstandingRemark(e.target.value);
                        }}
                        required
                        display={disabled}
                        fullWidth
                        size="small"
                        multiline
                        minRows={2}
                    />
                </Box>
            </Collapse>
        </Box>
    );
}
